import { useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Menu } from "@base-ui/react/menu";
import { ArrowClockwiseIcon, ArrowRightIcon, ArchiveIcon, DotsThreeIcon, GitBranchIcon, LockSimpleIcon, CloudIcon, MagnifyingGlassIcon, PencilSimpleIcon, PlayIcon, PushPinIcon } from "@phosphor-icons/react";
import { AgentCopyButton } from "../../../../../packages/design/src/AgentCopyButton";
import { authoringPrompt } from "@method-ai-hq/sdk/authoring-prompt";
import { teammateSetupPrompt } from "../../../../../packages/contracts/src/local-prompt";
import type { Inbox, MethodOverview } from "../../../../../packages/contracts/src/dashboard";
import { api, type WorkflowSummary } from "../../api";
import { useDashboard } from "../../Dashboard";
import { LoadingLines } from "../../Loading";
import { useWorkspacePrefetch } from "../../workspace-queries";
import { Button, buttonVariants, cn } from "../ui/button";
import { RunPopover } from "./RunPopover";
import { RunSquares, squareClass } from "./RunSquares";
import { rhythm } from "./rhythm";
import { duration, when, at } from "./format";

type View = "week" | "older" | "never";
const VIEWS: [View, string][] = [["week", "Used this week"], ["older", "Older"], ["never", "Never ran"]];
const NEEDS_LIMIT = 6;

const methodView = (w: WorkflowSummary): View => !w.overview?.last_run ? "never" : w.overview.runs_7d > 0 ? "week" : "older";
/** Newest activity first: the last run, or the last save for a Method that never ran. */
const activity = (w: WorkflowSummary) => w.overview?.last_run?.created_at ?? w.updated_at;
const byActivity = (a: WorkflowSummary, b: WorkflowSummary) => activity(b).localeCompare(activity(a)) || a.id.localeCompare(b.id);
const shortDate = (value: string) => new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric" });
const failedStatus = (status?: string) => status === "failed";
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** One small square as a mark: red last run failed, blue running or due. */
function Mark({ tone }: { tone: "attention" | "running" }) {
  return <span className="ico" aria-hidden="true"><span className="run-squares run-squares-mark"><i className={`run-square is-${tone}`} /></span></span>;
}

/** The Methods panel: what needs you, then each Method with its last result, rhythm and last 10 runs. */
export function MethodsPage() {
  const { workflows, workflowsLoading, workflowsError, inbox, refresh, setRename, setPins } = useDashboard();
  const [search, setSearch] = useState("");
  const [chosen, setChosen] = useState<View | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [open, setOpen] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const active = useMemo(() => workflows.filter(w => !w.archived_at), [workflows]);
  const counts = useMemo(() => {
    const counts: Record<View, number> = { week: 0, older: 0, never: 0 };
    for (const w of active) counts[methodView(w)]++;
    return counts;
  }, [active]);
  const view = chosen ?? (VIEWS.find(([key]) => counts[key] > 0)?.[0] ?? "week");
  const query = search.trim().toLowerCase();
  const rows = useMemo(() => active
    .filter(w => methodView(w) === view && `${w.name} ${w.desired_outcome ?? ""} ${w.overview?.facts?.goal ?? ""}`.toLowerCase().includes(query))
    .sort(byActivity), [active, view, query]);
  // Methods with the same name become one row, at the place of the newest one.
  const entries = useMemo(() => {
    const groups = new Map<string, WorkflowSummary[]>();
    for (const w of rows) groups.set(w.name, [...groups.get(w.name) ?? [], w]);
    return [...groups.values()];
  }, [rows]);
  const pinned = useMemo(() => active.filter(w => w.pinned_position != null)
    .sort((a, b) => a.pinned_position! - b.pinned_position!).map(w => w.id), [active]);
  const session = useQuery({ queryKey: ["session"], queryFn: api.me }).data;
  const firstMethod = !workflowsLoading && !workflowsError && active.length === 0;
  async function act(work: () => Promise<unknown>, failure: string) {
    setBusy(true); setError("");
    try { await work(); await refresh(); }
    catch { setError(failure); }
    finally { setBusy(false); }
  }
  const togglePin = (id: string) => void setPins(pinned.includes(id) ? pinned.filter(item => item !== id) : [...pinned, id]);
  const archive = (ids: string[]) => void act(() => Promise.all(ids.map(id => api.setArchived(id, true))), "Could not archive. Please try again.");
  const row = (w: WorkflowSummary, child = false) => <MethodRow key={w.id} workflow={w} child={child} pinned={pinned.includes(w.id)}
    onPin={() => togglePin(w.id)} onArchive={() => archive([w.id])} onRename={() => setRename(w)} disabled={busy} />;
  return (
    <section className="p-wrap methods-panel" aria-label="Methods">
      <div className="p-bar">
        <label className="list-search">
          <MagnifyingGlassIcon size={16} />
          <input aria-label="Filter methods" placeholder="Find a method…" value={search} onChange={e => setSearch(e.target.value)} />
        </label>
        <div className="p-seg" role="group" aria-label="Show Methods">
          {VIEWS.map(([key, label]) => <button key={key} type="button" aria-pressed={view === key} onClick={() => setChosen(key)}>{label}<b>{counts[key]}</b></button>)}
        </div>
        <Button variant="ghost" size="icon" className="ml-auto" disabled={refreshing} aria-label="Refresh methods"
          onClick={async () => { setRefreshing(true); try { await refresh(); } finally { setRefreshing(false); } }}>
          <ArrowClockwiseIcon className={refreshing ? "spin" : ""} size={17} />
        </Button>
      </div>
      {error && <p className="dash-notice" role="alert">{error}</p>}
      {firstMethod ? (
        <div className="dash-state methods-empty">
          <GitBranchIcon size={27} />
          <h2>Build your first Method</h2>
          <p>Paste these instructions into Codex, Claude Code, or Cursor.</p>
          <AgentCopyButton text={authoringPrompt} label="Copy instructions to agent" />
        </div>
      ) : workflowsError && !workflows.length ? (
        <div className="dash-state"><p>Could not load methods.</p></div>
      ) : <>
        {session?.welcome && <div className="dash-state" role="status">
          <h2>You joined {session.organization.name}</h2>
          <p>These are its Methods. To run them, set up Method on your computer once, then ask Claude or your coding agent to run a Method by its name.</p>
          <AgentCopyButton text={teammateSetupPrompt(session.organization.name)} label="Set up Claude for this workspace" />
        </div>}
        <NeedsYou methods={active} inbox={inbox} />
        <div className="p-section">
          <div className="m-row m-head"><span>Method · last result</span><span>Rhythm</span><span>Last 10 runs</span><span /></div>
          {workflowsLoading ? <LoadingLines rows={6} label="Loading methods" className="m-loading" />
            : !entries.length ? <div className="p-empty">{query
              ? <>No matching methods. <Button variant="ghost" onClick={() => setSearch("")}>Clear filter</Button></>
              : view === "week" ? "No Method ran this week." : view === "older" ? "No older Methods." : "Every Method has run."}</div>
            : entries.map(group => {
              if (group.length === 1) return row(group[0]!);
              const name = group[0]!.name, expanded = open.includes(name);
              const newest = [...group].sort((a, b) => b.created_at.localeCompare(a.created_at))[0]!;
              const runs = group.reduce((sum, w) => sum + Math.max(w.overview?.recent.length ?? 0, w.overview?.run_times.length ?? 0), 0);
              return <div key={`group:${name}`} className="m-group">
                <div className="grp">
                  <button type="button" className="grp-toggle" aria-expanded={expanded}
                    onClick={() => setOpen(current => expanded ? current.filter(item => item !== name) : [...current, name])}>
                    <span className="caret" aria-hidden="true">{expanded ? "▾" : "▸"}</span><b>{name}</b> · {group.length} Methods with this name, {plural(runs, "run")}
                  </button>
                  <Button variant="secondary" disabled={busy} onClick={() => archive(group.filter(w => w.id !== newest.id).map(w => w.id))}>
                    Keep the newest, archive {group.length - 1}
                  </Button>
                </div>
                {expanded && group.map(w => row(w, true))}
              </div>;
            })}
        </div>
      </>}
    </section>
  );
}

function NeedsYou({ methods, inbox }: { methods: WorkflowSummary[]; inbox?: Inbox }) {
  const items: { key: string; tone: "attention" | "running"; text: ReactNode; action: ReactNode }[] = [];
  const failed = new Set(inbox?.failures.map(failure => failure.method_id));
  for (const failure of inbox?.failures ?? []) items.push({
    key: `failure:${failure.run_id}`, tone: "attention",
    text: <><b>{failure.method_name}</b> <span className="why">· The last run failed {failure.stopped_step ? <>at <i>{failure.stopped_step.name}</i> </> : ""}{at(failure.created_at)}{failure.failure ? `: ${failure.failure.headline.replace(/\.$/, "")}` : ""}.</span></>,
    action: <Link className={buttonVariants({ variant: "secondary" })} to="/runs/$runId" params={{ runId: failure.run_id }}>Open the run</Link>,
  });
  for (const w of methods) {
    const o = w.overview;
    if (!o) continue;
    if (o.last_run?.status === "running") items.push({
      key: `running:${o.last_run.id}`, tone: "running",
      text: <><b>{w.name}</b> <span className="why">· Running since {when(o.last_run.created_at)}{o.last_run.summary?.current_step ? <>, at <i>{o.last_run.summary.current_step}</i></> : ""}.{o.typical_ms !== null ? ` It usually takes about ${duration(o.typical_ms)}.` : ""}</span></>,
      action: <Link className={buttonVariants({ variant: "secondary" })} to="/runs/$runId" params={{ runId: o.last_run.id }}>Open the run</Link>,
    });
    const r = rhythm(o.run_times);
    if (r?.due && o.last_run?.status !== "running" && !failed.has(w.id)) {
      const attention = o.recent.filter(status => squareClass(status) === "is-attention").length;
      items.push({
        key: `due:${w.id}`, tone: "running",
        text: <><b>{w.name}</b> <span className="why">· Due. You ran it {r.label.toLowerCase()}, and the last run was {shortDate(o.run_times[0] ?? o.last_run!.created_at)}.{attention ? ` ${attention} of its last 10 runs needed attention.` : ""}</span></>,
        action: <RunButton workflow={w} overview={o} />,
      });
    }
  }
  const order = { attention: 0, running: 1 };
  items.sort((a, b) => order[a.tone] - order[b.tone]);
  const shown = items.slice(0, NEEDS_LIMIT);
  const closed = (inbox?.closed ?? []).slice(0, Math.max(0, NEEDS_LIMIT - shown.length));
  if (!shown.length && !closed.length) return null;
  return <div className="p-section needs-you">
    <div className="p-h"><span>Needs you</span><small>{items.length > shown.length ? <Link to="/inbox">{items.length - shown.length} more in the Inbox</Link> : "From your recent runs"}</small></div>
    {shown.map(item => <div className="need" key={item.key}><Mark tone={item.tone} /><div>{item.text}</div><div className="acts">{item.action}</div></div>)}
    {closed.map(item => <div className="closed-line" key={item.failed_run_id}>
      <span className="run-squares run-squares-mark" aria-hidden="true"><i className="run-square" /></span>
      <span>Closed by itself: <b>{item.method_name}</b> stopped {item.stopped_step ? <>at <i>{item.stopped_step.name}</i> </> : ""}on version {item.failed_version}. {plural(item.passed_count, "newer run")} passed that step.</span>
    </div>)}
  </div>;
}

function RunButton({ workflow, overview }: { workflow: WorkflowSummary; overview?: MethodOverview | null }) {
  return <RunPopover
    target={{ methodId: workflow.id, methodName: workflow.name, versionId: workflow.current_version_id ?? null, facts: overview?.facts ?? null,
      typicalMs: overview?.typical_ms ?? null, lastResultRunId: overview?.last_result?.run_id ?? null }}
    trigger={<Button aria-label={`Run ${workflow.name}`}><PlayIcon size={13} weight="fill" />Run</Button>} />;
}

function MethodRow({ workflow: w, child, pinned, onPin, onArchive, onRename, disabled }: {
  workflow: WorkflowSummary; child: boolean; pinned: boolean; disabled: boolean;
  onPin: () => void; onArchive: () => void; onRename: () => void;
}) {
  const navigate = useNavigate();
  const prefetch = useWorkspacePrefetch();
  const o = w.overview;
  const facts = o?.facts ?? null;
  const last = o?.last_run ?? null;
  const r = o ? rhythm(o.run_times) : null;
  const result = o?.last_result;
  const goal = facts?.goal ?? w.desired_outcome;
  const detail = !last ? null
    : last.status === "running" ? <span className="live">Running now</span>
    : failedStatus(last.status) ? <span className="bad">Failed · {when(last.created_at)}</span>
    : r?.due ? <span className="live">{r.detail}</span>
    : <span>{r?.detail?.startsWith("Next about") ? r.detail : when(last.created_at)}</span>;
  const open = () => void navigate({ to: "/methods/$workflowId", params: { workflowId: w.id } });
  return <div className={cn("m-row", child && "m-child")}>
    <div className="m-main">
      <Link className="m-name" to="/methods/$workflowId" params={{ workflowId: w.id }} onMouseEnter={() => prefetch.method(w.id)} onFocus={() => prefetch.method(w.id)}>{w.name}</Link>
      <div className="m-res">{result?.content === "device" ? <i>Result kept on your computer</i>
        : result ? <>{result.label && <i>{result.label} · </i>}{result.preview ?? <i>{when(result.created_at)}</i>}</>
        : <i>{goal || "No result yet"}</i>}</div>
      <div className="m-meta">
        {facts?.required_inputs.length ? <><span>You give</span>{facts.required_inputs.map(input => <span key={input} className="pill">{input}</span>)}</> : <span>Nothing to give</span>}
        <span>·</span>
        {facts?.run_data === "device" ? <span className="where"><LockSimpleIcon size={12} />On your computer, content stays there</span>
          : o?.placement === "cloud" ? <span className="where"><CloudIcon size={12} />Runs in Method Cloud</span>
          : <span className="where">Runs on your computer</span>}
      </div>
    </div>
    <div className="rh"><b>{r?.label ?? (last ? "Not in 30 days" : "Never ran")}</b>{detail}</div>
    <div>{o ? <><RunSquares statuses={o.recent} /><div className="strip-sub">{plural(o.runs_7d, "run")} this week</div></> : null}</div>
    <div className="m-act">
      <RunButton workflow={w} overview={o} />
      <Menu.Root>
        <Menu.Trigger className={buttonVariants({ variant: "ghost", size: "icon" })} aria-label={`Actions for ${w.name}`}><DotsThreeIcon size={21} /></Menu.Trigger>
        <Menu.Portal>
          <Menu.Positioner align="end" sideOffset={5}>
            <Menu.Popup className="dash-menu">
              <Menu.Item onClick={open}><ArrowRightIcon size={15} />Open method</Menu.Item>
              <Menu.Item onClick={onPin}><PushPinIcon size={15} />{pinned ? "Unpin method" : "Pin method"}</Menu.Item>
              <Menu.Item disabled={disabled} onClick={onArchive}><ArchiveIcon size={15} />Archive</Menu.Item>
              <Menu.Item onClick={onRename}><PencilSimpleIcon size={15} />Rename</Menu.Item>
            </Menu.Popup>
          </Menu.Positioner>
        </Menu.Portal>
      </Menu.Root>
    </div>
  </div>;
}
